/**
 * DashboardLayout.tsx — the app shell.
 * Sidebar: brand, department switcher, workspace nav, user footer.
 * Topbar:  page title + capsule search + Dashboard/MC segmented + theme + icons.
 *
 * S-TIER FOUNDATION (slice 1): restyled onto the .k-* component contract in
 * src/styles/klaxiora.css — 236px sidebar, mono group labels, .k-navrow rows,
 * .k-search capsule with its ⌘K chip, .k-segmented controls, .k-topbar and the
 * .k-page padding. This is a RESTYLE IN PLACE: the information architecture is
 * untouched. DEPARTMENTS, NAV_ICONS, PAGE_TITLES, navModel, NAV_HIDDEN and
 * SHOW_PLATFORM_SECTION are all exactly as they were — this slice changes how
 * the shell looks, never what it contains or where it goes.
 *
 * Two things are genuinely new, both required by the system:
 *   · The THEME CAPSULE (dark/light), backed by theme/theme.ts.
 *   · The count-chip SLOT on nav rows (.k-count). It is a slot and nothing
 *     more: no nav item has an honest per-item count wired yet, so nothing
 *     renders anywhere today. Each department slice wires its own real number.
 *     Rendering a fabricated count to match a reference screen would break the
 *     data-honesty invariant, which outranks the visual authority (CLAUDE.md).
 *
 * Colour/size values still flow from src/theme/tokens.ts, which now re-exports
 * the design system's custom properties — so this shell themes for free.
 */

import {
  Bell, Search, Settings, Calendar, BarChart2, Zap, LogOut,
  LayoutGrid, Inbox as InboxIcon, Columns3, Users, ChartColumn, Activity,
  Archive, FileText, Send, CircleCheck, CircleX, Wallet, Target, GitBranch,
  Briefcase, UserPlus, Ticket, Banknote, GraduationCap, FileSearch, Bot, Building2,
  CalendarClock, Linkedin, Megaphone, TrendingUp, Mail, ScrollText, Scale,
  Gavel, History, BookOpen, ClipboardCheck, AlertTriangle, Package,
  CalendarDays, Moon, Sun, Network, type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { accent, brand, edge, surface, track } from '../../theme/tokens'
import { setTheme, useTheme } from '../../theme/theme'
import { activeDepartment } from './navModel'

// ── Department nav structure ──────────────────────────────────────────────────

// feature: when set, the item renders only if the tenant holds that enabled
// feature key (GET /v1/me features). Cosmetic — the server 403 is the boundary.
// count: the .k-count chip slot. OPTIONAL and unset everywhere today — the
// chip renders ONLY when a real number is passed, never a placeholder or a
// zero standing in for "unknown" (data honesty outranks the reference screens).
interface NavItem  { id: string; label: string; feature?: string; count?: number }
interface Department {
  id: string
  label: string
  items: NavItem[]
}

// ── Sidebar visibility flags (§14.D) — HIDDEN, never deleted. Flip a flag to
// restore. Hiding 'inbox' removes the sidebar path to the LIVE triage queue
// (inbox.triage); hiding the Platform section removes System Health / Run
// Analytics / Model Usage from the shell. Routes, PAGE_TITLES entries and
// page components all remain — the pages are unlinked, not gone.
const NAV_HIDDEN: Record<string, boolean> = { inbox: true }
const SHOW_PLATFORM_SECTION = false

const DEPARTMENTS: Department[] = [
  {
    // Tenant-level, department-agnostic: the triage queue reviews inbound
    // emails BEFORE they belong to any department — hence its own section.
    id: 'inbox', label: 'Inbox',
    items: [
      { id: 'inbox.triage', label: 'Triage' },
    ],
  },
  {
    id: 'sales', label: 'Sales',
    items: [
      { id: 'sales.dashboard',  label: 'Dashboard' },
      // L8-4 LOCKED (Klemen): Inbound Leads sits SECOND, immediately after
      // Dashboard — top-of-funnel, per the full handoff tab order.
      { id: 'sales.leads',      label: 'Inbound Leads' },
      { id: 'sales.pipeline',   label: 'Pipeline' },
      { id: 'sales.contacts',   label: 'Contacts' },
      // Slice A (Phase 1 / 1.1): Accounts sits after Contacts — the entity
      // the contacts and deals link to. Every sales baseline moved one row.
      { id: 'sales.accounts',   label: 'Accounts' },
      { id: 'sales.reports',    label: 'Reports' }, // mockup nav order (R6-6): after Contacts
      { id: 'sales.activities', label: 'Activities' },
      { id: 'sales.archive',    label: 'Archive' },
    ],
  },
  {
    id: 'finance', label: 'Finance',
    items: [
      { id: 'finance.overview',  label: 'Overview' },
      { id: 'finance.invoices',  label: 'Invoices' },
      { id: 'finance.issued-invoices', label: 'Issued Invoices' },
      { id: 'finance.approvals', label: 'Approvals' },
      { id: 'finance.rejections', label: 'Rejections' },
      { id: 'finance.expenses',  label: 'Expenses' },
      { id: 'finance.budgets',   label: 'Budgets' },
      { id: 'finance.reports',   label: 'Reports' },
    ],
  },
  {
    id: 'hr', label: 'HR',
    items: [
      { id: 'hr.overview',    label: 'Overview' },
      { id: 'hr.employees',   label: 'Employees' },
      { id: 'hr.lifecycle',   label: 'Lifecycle' },
      { id: 'hr.job-postings', label: 'Job Postings' },
      { id: 'hr.onboarding',  label: 'Onboarding' },
      // Org Chart sits before Tickets — the mockup's relative order (:3395).
      { id: 'hr.org-chart',   label: 'Org Chart' },
      { id: 'hr.tickets',     label: 'Tickets' },
    ],
  },
  {
    // Directly after HR, deliberately: payroll computes over HR's employee
    // records (profiles feed the ledger), so adjacency mirrors the data flow.
    // A standalone section, not an HR item — the pay-run view is a run across
    // employees, not a property of one.
    id: 'payroll', label: 'Payroll',
    items: [
      { id: 'payroll.pay-run', label: 'Pay Run' },
    ],
  },
  {
    id: 'recruitment', label: 'Recruitment',
    items: [
      { id: 'recruitment.overview',  label: 'Overview' },
      { id: 'recruitment.jobs',      label: 'Job Postings' },
      { id: 'recruitment.candidates',label: 'Candidates' },
      { id: 'recruitment.providers', label: 'Providers', feature: 'learning_providers' },
      { id: 'recruitment.cv',        label: 'CV Screening' },
    ],
  },
  {
    id: 'operations', label: 'Operations',
    items: [
      { id: 'operations.overview',  label: 'Overview' },
      { id: 'operations.agents',    label: 'Agents' },
      { id: 'operations.runs',      label: 'Active Runs' },
      { id: 'operations.scheduled', label: 'Schedules' },
      // M5a: Field Schedule — the mockup's Schedules tab slot (:3421,
      // between Active Runs and SOPs). Named apart from the cron
      // "Schedules" row above, which is agent-ops and untouched (the §B.4
      // name-collision rule: the field entity is tasks, not schedules).
      { id: 'operations.tasks',     label: 'Field Schedule' },
      // M1: SOPs — first real field-ops page; sits after Schedules per the
      // mockup's Operations nav order (klaxiora-crm-mockup-full.html :3422).
      { id: 'operations.sops',      label: 'SOPs' },
      // M3: Incidents — between SOPs and Quality Checks per the mockup tab
      // order (:3422-3425: sops, incidents, assets, quality).
      { id: 'operations.incidents', label: 'Incidents' },
      // M4: Assets — between Incidents and Quality Checks per the mockup tab
      // order (:3422-3425: sops, incidents, assets, quality).
      { id: 'operations.assets',    label: 'Assets' },
      // M2: Quality Checks — last mockup tab (:3425); LinkedIn stays last
      // as the live-only page.
      { id: 'operations.quality',   label: 'Quality Checks' },
      { id: 'operations.linkedin',  label: 'LinkedIn' },
    ],
  },
  {
    id: 'marketing', label: 'Marketing',
    items: [
      { id: 'marketing.overview',   label: 'Overview' },
      { id: 'marketing.campaigns',  label: 'Campaigns' },
      { id: 'marketing.analytics',  label: 'Analytics' },
      { id: 'marketing.email',      label: 'Email' },
    ],
  },
  {
    id: 'legal', label: 'Legal',
    items: [
      { id: 'legal.overview',   label: 'Overview' },
      { id: 'legal.contracts',  label: 'Contracts' },
      { id: 'legal.compliance', label: 'Compliance' },
    ],
  },
  {
    id: 'bids', label: 'Bids',
    items: [
      { id: 'bids.overview',   label: 'Overview' },
      { id: 'bids.active',     label: 'Active Bids' },
      { id: 'bids.history',    label: 'History' },
    ],
  },
]

// ── Workspace item icons (S14-2) — the mockup's nav is icon+label
// (klaxiora-crm-mockup-full.html :3351+, 17px .nav-item .icon). Keyed by
// route id; lucide equivalents of the mockup's ICON_* where the page exists
// there, in-spirit picks for live-only pages. Departments cards stay
// icon-less (the mockup's are).
const NAV_ICONS: Record<string, LucideIcon> = {
  'overview.home':          LayoutGrid,
  'sales.dashboard':        LayoutGrid,
  'sales.leads':            InboxIcon,
  'sales.pipeline':         Columns3,
  'sales.contacts':         Users,
  'sales.accounts':         Building2,
  'sales.reports':          ChartColumn,
  'sales.activities':       Activity,
  'sales.archive':          Archive,
  'finance.overview':       LayoutGrid,
  'finance.invoices':       FileText,
  'finance.issued-invoices': Send,
  'finance.approvals':      CircleCheck,
  'finance.rejections':     CircleX,
  'finance.expenses':       Wallet,
  'finance.budgets':        Target,
  'finance.reports':        ChartColumn,
  'hr.overview':            LayoutGrid,
  'hr.employees':           Users,
  'hr.lifecycle':           GitBranch,
  'hr.job-postings':        Briefcase,
  'hr.onboarding':          UserPlus,
  'hr.org-chart':           Network,
  'hr.tickets':             Ticket,
  'payroll.pay-run':        Banknote,
  'recruitment.overview':   LayoutGrid,
  'recruitment.jobs':       Briefcase,
  'recruitment.candidates': Users,
  'recruitment.providers':  GraduationCap,
  'recruitment.cv':         FileSearch,
  'operations.overview':    LayoutGrid,
  'operations.agents':      Bot,
  'operations.runs':        Activity,
  'operations.scheduled':   CalendarClock,
  'operations.tasks':       CalendarDays,
  'operations.sops':        BookOpen,
  'operations.incidents':   AlertTriangle,
  'operations.assets':      Package,
  'operations.quality':     ClipboardCheck,
  'operations.linkedin':    Linkedin,
  'marketing.overview':     LayoutGrid,
  'marketing.campaigns':    Megaphone,
  'marketing.analytics':    TrendingUp,
  'marketing.email':        Mail,
  'legal.overview':         LayoutGrid,
  'legal.contracts':        ScrollText,
  'legal.compliance':       Scale,
  'bids.overview':          LayoutGrid,
  'bids.active':            Gavel,
  'bids.history':           History,
}

// Page title map
const PAGE_TITLES: Record<string, string> = {
  'overview.home':          'Dashboard',
  'inbox.triage':           'Triage',
  'overview.health':        'System Health',
  'automation.agents':      'All Agents',
  'automation.runs':        'Active Runs',
  'automation.scheduled':   'Scheduled Jobs',
  'scraping.linkedin':      'LinkedIn',
  'analytics.performance':  'Run Performance',
  'analytics.models':       'Model Usage',
  'analytics.feedback':     'Feedback Data',
  'sales.dashboard':        'Sales Dashboard',
  'sales.leads':            'Inbound Leads',
  'sales.pipeline':         'Pipeline',
  'sales.contacts':         'Contacts',
  'sales.accounts':         'Accounts',
  'sales.reports':          'Reports',
  'sales.activities':       'Activities',
  'sales.archive':          'Archive',
  'operations.agents':      'Agents',
  'operations.runs':        'Active Runs',
  'operations.scheduled':   'Schedules',
  'operations.tasks':       'Field Schedule',
  'operations.sops':        'SOPs',
  'operations.incidents':   'Incidents',
  'operations.assets':      'Assets',
  'operations.quality':     'Quality Checks',
  'operations.linkedin':    'LinkedIn',
  'settings.company':       'Settings',
  'finance.issued-invoices': 'Issued Invoices',
  'recruitment.providers':  'Learning Providers',
  'payroll.pay-run':        'Pay Run',
  'hr.org-chart':           'Org Chart',
}

// ── One sidebar row ───────────────────────────────────────────────────────────
// `current` drives .k-navrow[aria-current="page"] — the system keys its active
// state on a real accessibility attribute, not a class.
//
// `count` is the .k-count chip SLOT and is the data-honesty boundary: the chip
// renders only when a real number arrives, so today nothing renders anywhere.
// Each department slice wires its own count from its own read path.
function NavRow({ id, label, Icon, current, count, trailing, onNavigate }: {
  id: string
  label: string
  Icon?: LucideIcon
  current: boolean
  count?: number
  trailing?: React.ReactNode
  onNavigate: (page: string) => void
}) {
  return (
    <button
      onClick={() => onNavigate(id)}
      aria-current={current ? 'page' : undefined}
      className="k-navrow w-full text-left text-13 cursor-pointer bg-transparent border-0"
    >
      <span className="flex items-center gap-[10px] min-w-0">
        {Icon && <Icon size={15} className="flex-shrink-0" />}
        <span className="k-truncate">{label}</span>
      </span>
      {typeof count === 'number' ? <span className="k-count">{count}</span> : trailing}
    </button>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface DashboardLayoutProps {
  activePage: string
  onNavigate: (page: string) => void
  children: React.ReactNode
  onSwitchMode: () => void
  /** Clears the stored token and returns to the login page. Omitted in stub mode. */
  onLogout?: () => void
  /** The backend-resolved tenant name (GET /v1/me). null until loaded — nothing renders. */
  tenantName?: string | null
  /** The session user's display name (GET /v1/me). Unset for WorkOS users with no name — email is the fallback. */
  userDisplayName?: string | null
  /** The session user's email (GET /v1/me). Absent for dev-token operators — no name line renders. */
  userEmail?: string | null
  /**
   * Feature-entitlement check for feature-tagged nav items (GET /v1/me
   * features). FAIL CLOSED: omitted, or /me not yet loaded, hides every
   * tagged item — the server-side 403 stays the real boundary.
   */
  hasFeature?: (key: string) => boolean
  /**
   * The scoped scroll-model variant for viewport-owning screens (S-Tier slice
   * 3 / ADOPTION.md §6.5: the pipeline board). The .dash-shell grid already
   * bounds the viewport; what this flips is the MAIN region: normally it is
   * the page scroller (overflow-y-auto + .k-page padding), but a
   * viewport-owning page brings its own nested scrollers (.k-board /
   * .k-lane), so main stops scrolling and stops padding — the page fills it
   * exactly. Dashboard.tsx sets this per RENDERED page (not per route id:
   * sales.pipeline renders the normally-scrolling Deal Detail while a deal is
   * drilled). Every other screen keeps the scrolling default.
   */
  viewportOwning?: boolean
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DashboardLayout({ activePage, onNavigate, children, onSwitchMode, onLogout, tenantName, userDisplayName, userEmail, hasFeature, viewportOwning = false }: DashboardLayoutProps) {
  const theme = useTheme()

  // Feature-tagged nav items render only for entitled tenants (fail closed:
  // no hasFeature or no /me yet → tagged items hidden). Untagged items are
  // unaffected; a department whose items all hide collapses away entirely.
  const visibleDepartments = DEPARTMENTS
    .filter(dept => !NAV_HIDDEN[dept.id])
    .map(dept => ({ ...dept, items: dept.items.filter(item => !item.feature || (hasFeature?.(item.feature) ?? false)) }))
    .filter(dept => dept.items.length > 0)
  // Derived, never stored (§14.C): the active department is a pure function
  // of the route, so the card highlight and the Workspace list cannot desync.
  const activeDept = activeDepartment(visibleDepartments, activePage)

  const userLabel = userDisplayName || userEmail || null
  const userInitials = userDisplayName
    ? userDisplayName.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : userEmail
      ? userEmail.split('@')[0].slice(0, 2).toUpperCase()
      : null

  const pageTitle = PAGE_TITLES[activePage] ?? (() => {
    const parts = activePage.split('.')
    return parts[parts.length - 1].replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
  })()

  return (
    <div className="dash-shell">
      {/* ── Sidebar — .k-sidebar treatment (18px 12px 10px, --panel, hairline) ── */}
      <aside
        className="row-start-1 row-end-3 col-start-1 col-end-2 flex flex-col overflow-hidden pt-[18px] px-3 pb-[10px]"
        style={{
          background: surface.sidebar,
          borderRight: `1px solid ${edge.sidebar}`,
          gridArea: 'sidebar',
        }}
      >
        {/* Brand block — full-mockup wordmark (:57-68): tracked extrabold mark,
            the gradient X (a brand TOKEN — its two stops are system text
            tokens, so the mark re-reads correctly in light mode), an uppercase
            tracked sub, the block underlined with the system hairline. */}
        <div className="px-[10px] pb-4 mb-[18px] border-b flex-shrink-0" style={{ borderColor: edge.rule }}>
          <div className="text-20 font-extrabold text-ink-primary leading-tight tracking-[0.03em]">
            KLA<span style={{ background: brand['mark-gradient'], WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>X</span>IORA
          </div>
          <div className="k-navgroup-label" style={{ padding: '4px 0 0' }}>
            {activeDept ? activeDept.label.toUpperCase() : 'MISSION CONTROL'}
          </div>
        </div>

        {/* Nav */}
        <nav aria-label="Primary navigation" className="flex-1 overflow-y-auto flex flex-col gap-[2px]" style={{ scrollbarWidth: 'none' }}>
          {/* Pinned Dashboard (S14-1 option 3) — the one accepted deviation
              from the mockup's strict two-section model: keeps the O-1
              landing (overview.home) one click away. */}
          <NavRow
            id="overview.home"
            label="Dashboard"
            Icon={LayoutGrid}
            current={activePage === 'overview.home'}
            onNavigate={onNavigate}
          />

          {/* DEPARTMENTS — the mockup's flat switcher (:3464-3481): every
              department always visible, no expansion; clicking one lands on
              its FIRST page (switchDepartment semantics, :3495-3501).
              The active department carries aria-current="true" ("current item
              in a set"), NOT "page" — exactly one row in the sidebar may claim
              to be the page, and that is the workspace row below. Both wear
              the same .k-navrow active treatment. */}
          <div className="k-navgroup-label mt-5">DEPARTMENTS</div>
          <div role="group" aria-label="Department switcher" className="flex flex-col gap-[2px]">
            {visibleDepartments.map(dept => {
              const current = activeDept?.id === dept.id
              return (
                <button
                  key={dept.id}
                  onClick={() => onNavigate(dept.items[0].id)}
                  aria-current={current ? 'true' : undefined}
                  className="k-navrow w-full text-left text-13 cursor-pointer bg-transparent border-0"
                >
                  <span className="k-truncate">{dept.label}</span>
                  {current && <span className="k-dot" aria-hidden="true" />}
                </button>
              )
            })}
          </div>

          {/* WORKSPACE — only the active department's pages (renderNav,
              :3483-3493), icon+label. Hidden on dept-less routes
              (overview.home / settings) — nothing honest to show. */}
          {activeDept && (
            <div className="mt-5 flex flex-col gap-[2px]">
              <div className="k-navgroup-label">WORKSPACE</div>
              {activeDept.items.map(item => (
                <NavRow
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  Icon={NAV_ICONS[item.id] ?? LayoutGrid}
                  current={activePage === item.id}
                  count={item.count}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}

          {/* Platform section — flag-hidden (§14.D), not deleted. */}
          {SHOW_PLATFORM_SECTION && (
            <div className="mt-5 flex flex-col gap-[2px]">
              <div className="k-navgroup-label">PLATFORM</div>
              {[
                { id: 'overview.health',      label: 'System Health',
                  trailing: <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent['health-dot'] }} /> },
                // M7b: operations.agents now renders the field-team page, so
                // the AI-agent AgentsList lives on at its dormant
                // automation.agents alias — this row is its only sidebar path.
                { id: 'automation.agents',    label: 'AI Agents' },
                { id: 'analytics.performance',label: 'Run Analytics' },
                { id: 'analytics.models',     label: 'Model Usage' },
              ].map(item => (
                <NavRow
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  current={activePage === item.id}
                  trailing={item.trailing}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}
        </nav>

        {/* Pinned user footer — the session user (/v1/me): display name, else email; nothing for dev-token operators. */}
        <div className="mt-auto pt-[14px] border-t flex items-center gap-[10px] px-[10px] flex-shrink-0" style={{ borderColor: edge.rule }}>
          {userInitials && (
            <div className="w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0" style={{ background: track.DEFAULT, borderColor: edge.sidebar }}>
              <span className="text-12 font-bold leading-none text-ink-primary">{userInitials}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            {userLabel && (
              <div className="text-13 font-semibold text-ink-primary leading-tight truncate">{userLabel}</div>
            )}
            {/* Active tenant — the session scope the backend actually resolved (/v1/me). */}
            {tenantName && (
              <div className="k-navgroup-label truncate" style={{ padding: '2px 0 0' }} title="Active tenant">
                {tenantName.toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── Topbar — .k-topbar treatment (11px 24px, --panel, hairline) ── */}
      <header
        className="flex items-center gap-[14px] px-6"
        style={{
          gridArea: 'header',
          background: surface.header,
          borderBottom: `1px solid ${edge.sidebar}`,
          height: '56px',
        }}
      >
        <h1 className="text-page-title font-semibold text-ink-primary flex-shrink-0">{pageTitle}</h1>

        <div className="flex-1 flex">
          {/* .k-search — the capsule takes the focus ring on the input's
              behalf, so the affordance follows the capsule's shape. */}
          <div className="k-search w-64">
            <Search size={13} className="flex-shrink-0" />
            <input
              className="flex-1 min-w-0 bg-transparent border-0 p-0 text-ink-body placeholder:text-ink-faint"
              style={{ fontSize: '12.5px' }}
              placeholder="Search…"
            />
            <span className="k-kbd flex-shrink-0">⌘K</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Shell switcher — .k-segmented, active keyed on aria-selected. */}
          <div className="k-segmented" role="tablist" aria-label="Application shell">
            <button role="tab" aria-selected="true" className="flex items-center gap-2 cursor-pointer bg-transparent border-0">
              <BarChart2 size={11} /> Dashboard
            </button>
            <button
              role="tab"
              aria-selected="false"
              onClick={onSwitchMode}
              className="flex items-center gap-2 cursor-pointer bg-transparent border-0"
            >
              <Zap size={11} /> Mission Control
            </button>
          </div>

          {/* Theme capsule — gss.theme, dark default (theme/theme.ts). */}
          <div className="k-segmented" role="tablist" aria-label="Colour theme">
            <button
              role="tab"
              aria-selected={theme === 'dark'}
              onClick={() => setTheme('dark')}
              className="flex items-center cursor-pointer bg-transparent border-0"
              style={{ paddingLeft: '10px', paddingRight: '10px' }}
              aria-label="Dark theme"
              title="Dark theme"
            >
              <Moon size={12} />
            </button>
            <button
              role="tab"
              aria-selected={theme === 'light'}
              onClick={() => setTheme('light')}
              className="flex items-center cursor-pointer bg-transparent border-0"
              style={{ paddingLeft: '10px', paddingRight: '10px' }}
              aria-label="Light theme"
              title="Light theme"
            >
              <Sun size={12} />
            </button>
          </div>

          <button className="w-8 h-8 flex items-center justify-center rounded-full text-ink-secondary hover:bg-surface-nav-hover transition-colors" onClick={() => toast.info('Calendar coming soon')}>
            <Calendar size={15} />
          </button>
          <button
            className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
              activePage === 'settings.company'
                ? 'text-ink-primary bg-surface-nav-active'
                : 'text-ink-secondary hover:bg-surface-nav-hover'
            }`}
            onClick={() => onNavigate('settings.company')}
            aria-label="Settings"
            title="Settings"
          >
            <Settings size={15} />
          </button>
          <button className="relative w-8 h-8 flex items-center justify-center rounded-full text-ink-secondary hover:bg-surface-nav-hover transition-colors">
            <Bell size={15} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-danger" />
          </button>
          {onLogout && (
            <button
              onClick={onLogout}
              className="w-8 h-8 flex items-center justify-center rounded-full text-ink-secondary hover:bg-danger-bg hover:text-danger transition-colors"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut size={15} />
            </button>
          )}
        </div>
      </header>

      {/* ── Main — .k-page padding (22px 24px 44px). The flex/gap half of
             .k-page is deliberately NOT applied: pages own their own internal
             spacing today, and adding a parent gap would double-space them.
             A viewportOwning page (the pipeline board) instead gets an
             unpadded, non-scrolling main it fills exactly — its .k-pipeline
             head/board carry their own padding and scrolling (§6.5). ── */}
      <main
        className={viewportOwning ? 'overflow-hidden' : 'overflow-y-auto'}
        style={{
          gridArea: 'main',
          background: surface.content,
          padding: viewportOwning ? 0 : '22px 24px 44px',
        }}
      >
        {children}
      </main>
    </div>
  )
}
